import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../variable-overrides'
import type { Feature } from '../feature'
import type * as models from '../models'

/**
 * Time in milliseconds to debounce the rendering process either on
 * pageVariables or pageContext change
 *
 * @see {@link variableOverrides.Feature}
 */
const RENDERING_DEBOUNCE_TIME = 500

/**
 * Triggers the page rendering process when there are changes to the
 * {@link models.page.Rendering.Context} or whenever variables for given page
 * changes
 *
 * @see {@link models.page.Rendering.Context}, {@link variableOverrides.Feature}
 */
export const triggerPageRendering = store.effects.create(
  (feature: Feature, variablesFeature: variableOverrides.Feature) =>
    // TODO: Evaluate using the setContext action as the trigger for rendering
    // instead of relying on selector changes.
    feature.selectors.context.asObservable.pipe(
      rxjs.switchMap(pageRenderingContext => {
        // If there is no page rendering context, we cannot trigger the
        // rendering process
        if (!pageRenderingContext) {
          return rxjs.EMPTY
        }

        const { pageId, scenarioId, appId } = pageRenderingContext

        // get variables that are used for current rendering
        return variablesFeature.selectors
          .getVariableOverrides({
            page: {
              appId,
              pageId,
              scenarioId,
            },
          })
          .asObservable.pipe(
            // Prepare object with required data for rendering before debouncing
            // in the outer observable
            rxjs.map(pageVariables => ({
              pageRenderingContext,
              pageVariables,
            })),
          )
      }),
      // NOTE Debounce the rendering process to prevent excessive rendering
      // requests when variables are changed rapidly
      rxjs.debounceTime(RENDERING_DEBOUNCE_TIME),
      rxjs.map(({ pageVariables, pageRenderingContext }) =>
        feature.actions.render.from('triggerPageRendering (effect)').create({
          ...pageRenderingContext,
          variables: pageVariables,
        }),
      ),
    ),
)
